/* eslint-disable no-unused-vars */
import type { SupSingleVaultV3, SupWeb3TxSingleVault } from "@/types";
import React, { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import { getSuperformSimulation, type VaultSimulationState } from "@/lib/api/getSuperformSimulation";

interface VaultState {
  loading?: boolean;
  success?: boolean;
  result?: VaultSimulationState;
  error?: string;
}

interface SimulationState {
  loading: boolean;
  simulated: boolean;
  success: boolean;
  allDepositsPassed?: boolean;
  allWithdrawalsPassed?: boolean;
  getVaultState?: (vaultId: string) => VaultState | undefined;
  runSimulation?: (vaults: SupSingleVaultV3[]) => void;
}

const defaultState = {
  loading: false,
  simulated: false,
  success: false,
};

const SimulationContext = createContext<SimulationState>(defaultState);

export function useSimulationState() {
  return useContext(SimulationContext);
}

export default function SimulationState({ children }: PropsWithChildren) {
  const [simulationState, setSimulationState] = useState({ loading: false, simulated: false, success: false });
  const [simulatedVaults, setSimulatedVaults] = useState<Record<string, VaultState | undefined>>({});

  const getVaultState = useCallback(
    (vaultId: string) => {
      return simulatedVaults[vaultId];
    },
    [simulatedVaults]
  );

  const updateVaultState = useCallback((vaultId: string, state: Partial<VaultState>) => {
    setSimulatedVaults(prevState => ({ ...prevState, [vaultId]: { ...prevState[vaultId], ...state } }));
  }, []);

  const startSimulation = useCallback(() => {
    setSimulationState(simulationState => ({
      ...simulationState,
      loading: true,
      success: false,
      simulated: false,
    }));
    setSimulatedVaults({});
  }, []);

  const endSimulation = useCallback((success: boolean) => {
    setSimulationState(() => ({
      simulated: true,
      loading: false,
      success,
    }));
  }, []);

  const runSimulation = useCallback(
    async (vaults: SupWeb3TxSingleVault[]) => {
      startSimulation?.();

      const promises = vaults.map(async ({ id, superform_id }) => {
        updateVaultState?.(id, { loading: true });
        try {
          const simulationResult = await getSuperformSimulation(superform_id);
          const success = ["success", "skipped"].includes(simulationResult.status);
          updateVaultState?.(id, { loading: false, success, result: simulationResult, error: simulationResult.error });
          return success;
        } catch (error) {
          updateVaultState?.(id, { loading: false, success: false, error: `${error}` });
          return false;
        }
      });

      const results = await Promise.all(promises);

      endSimulation?.(results.every(success => success));
    },
    [startSimulation, endSimulation, updateVaultState]
  );

  const allDepositsPassed = useMemo(() => {
    if (!simulationState.simulated) return;

    return Object.values(simulatedVaults).reduce<boolean>(
      (acc, state = {}) => (acc ? state.result?.deposit !== undefined && ["skipped", "success"].includes(state.result?.deposit) : acc),
      true
    );
  }, [simulatedVaults, simulationState.simulated]);

  const allWithdrawalsPassed = useMemo(() => {
    if (!simulationState.simulated) return;

    return Object.values(simulatedVaults).reduce<boolean>(
      (acc, state = {}) => (acc ? state.result?.withdraw !== undefined && ["skipped", "success"].includes(state.result?.withdraw) : acc),
      true
    );
  }, [simulatedVaults, simulationState.simulated]);

  const contextValues = useMemo(
    () => ({
      ...simulationState,
      allDepositsPassed,
      allWithdrawalsPassed,
      getVaultState,
      runSimulation,
    }),
    [simulationState, allDepositsPassed, allWithdrawalsPassed, getVaultState, runSimulation]
  );

  return <SimulationContext.Provider value={contextValues}>{children}</SimulationContext.Provider>;
}
